/**
 * RNCM Dashboard - RN Case Manager Dashboard
 * 
 * Comprehensive dashboard for RN Case Managers with:
 * - Patient hotlist by priority and category
 * - Caseload overview with compliance scores
 * - Visit scheduling summary
 * - AI-powered predictive alerts
 * - Quick action capabilities
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  AlertCircle,
  Users,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  FileText,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  RefreshCw,
  Filter,
  ArrowLeft,
  Home,
  UserCheck,
  Stethoscope,
  ClipboardList,
  Bell,
  Brain,
  Zap,
  Target,
  Flag,
  MessageSquare,
  CalendarPlus,
  Shield,
  Heart
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  critical: { color: "bg-red-500", textColor: "text-red-400", label: "Critical" },
  high: { color: "bg-amber-500", textColor: "text-amber-400", label: "High" },
  medium: { color: "bg-blue-500", textColor: "text-blue-400", label: "Medium" },
  low: { color: "bg-slate-500", textColor: "text-slate-400", label: "Low" }
};

const CATEGORY_CONFIG = {
  overdue_tasks: { icon: Clock, label: "Overdue Tasks", color: "text-red-400" },
  compliance_risk: { icon: Shield, label: "Compliance Risk", color: "text-amber-400" },
  recert_due: { icon: Calendar, label: "Recert Due", color: "text-purple-400" },
  f2f_needed: { icon: UserCheck, label: "F2F Needed", color: "text-blue-400" },
  idg_due: { icon: Users, label: "IDG Due", color: "text-teal-400" },
  decline_risk: { icon: TrendingDown, label: "Decline Risk", color: "text-orange-400" },
  new_admission: { icon: Heart, label: "New Admission", color: "text-green-400" },
  symptom_alert: { icon: Activity, label: "Symptom Alert", color: "text-pink-400" }
};

const RISK_CONFIG = {
  high: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: AlertTriangle },
  medium: { color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: AlertCircle },
  low: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle }
};

export default function RNCMDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("hotlist");
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [hotlist, setHotlist] = useState([]);
  const [hotlistSummary, setHotlistSummary] = useState(null);
  const [caseload, setCaseload] = useState([]);
  const [caseloadSummary, setCaseloadSummary] = useState(null);
  const [visits, setVisits] = useState(null);
  const [predictiveAlerts, setPredictiveAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  
  // Filters
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("compliance_score");
  
  // Quick action dialog
  const [quickActionDialog, setQuickActionDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const headers = { "Authorization": `Bearer ${token}` };
      
      const [hotlistRes, caseloadRes, visitsRes, alertsRes] = await Promise.all([
        fetch(`${API}/api/rncm-dashboard/hotlist?limit=50`, { headers }),
        fetch(`${API}/api/rncm-dashboard/caseload?sort_by=${sortBy}`, { headers }),
        fetch(`${API}/api/rncm-dashboard/visits`, { headers }),
        fetch(`${API}/api/rncm-dashboard/predictive-alerts`, { headers })
      ]);
      
      if (hotlistRes.ok) {
        const data = await hotlistRes.json();
        setHotlist(data.hotlist || []);
        setHotlistSummary(data.summary);
      }
      
      if (caseloadRes.ok) {
        const data = await caseloadRes.json();
        setCaseload(data.caseload || []);
        setCaseloadSummary(data.summary);
      }
      
      if (visitsRes.ok) {
        const data = await visitsRes.json();
        setVisits(data);
      }
      
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setPredictiveAlerts(data.alerts || []);
        setAlertsSummary(data.summary);
      }
    } catch (error) {
      console.error("Dashboard load error:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sortBy]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  const executeQuickAction = async () => {
    if (!selectedPatient || !actionType) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/rncm-dashboard/quick-action/${actionType}?patient_id=${selectedPatient.patient_id}&notes=${encodeURIComponent(actionNotes)}`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || "Action completed");
        setQuickActionDialog(false);
        setActionNotes("");
        loadDashboard();
      } else {
        toast.error("Action failed");
      }
    } catch (error) {
      toast.error("Failed to execute action");
    } finally {
      setActionLoading(false);
    }
  };

  const openQuickAction = (patient, action) => {
    setSelectedPatient(patient);
    setActionType(action);
    setQuickActionDialog(true);
  };

  // Filter hotlist
  const filteredHotlist = hotlist.filter(p => {
    if (priorityFilter !== "all" && p.priority !== priorityFilter) return false;
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading RNCM Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Stethoscope className="w-6 h-6 text-teal-400" />
                  RNCM Dashboard
                </h1>
                <p className="text-sm text-slate-400">RN Case Manager Workstation</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Link to="/hos">
                <Button variant="outline" size="sm">
                  <Home className="w-4 h-4 mr-2" />
                  HOS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Patients</p>
                  <p className="text-2xl font-bold text-white">{caseloadSummary?.total_patients || 0}</p>
                </div>
                <Users className="w-8 h-8 text-teal-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">High Risk</p>
                  <p className="text-2xl font-bold text-red-400">{caseloadSummary?.high_risk || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Today's Visits</p>
                  <p className="text-2xl font-bold text-blue-400">{visits?.today || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Avg Compliance</p>
                  <p className="text-2xl font-bold text-green-400">{caseloadSummary?.average_compliance?.toFixed(0) || 0}%</p>
                </div>
                <Target className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 mb-6">
            <TabsTrigger value="hotlist" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Hotlist ({hotlistSummary?.total || 0})
            </TabsTrigger>
            <TabsTrigger value="caseload" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Caseload
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Visits
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              AI Alerts ({alertsSummary?.total || 0})
            </TabsTrigger>
          </TabsList>

          {/* Hotlist Tab */}
          <TabsContent value="hotlist">
            {/* Filters */}
            <div className="flex gap-4 mb-4">
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex-1" />
              
              <div className="flex gap-2">
                {hotlistSummary && (
                  <>
                    <Badge className="bg-red-500/20 text-red-400">
                      Critical: {hotlistSummary.critical}
                    </Badge>
                    <Badge className="bg-amber-500/20 text-amber-400">
                      High: {hotlistSummary.high}
                    </Badge>
                  </>
                )}
              </div>
            </div>

            {/* Hotlist */}
            <ScrollArea className="h-[600px]">
              <div className="space-y-3">
                {filteredHotlist.length === 0 ? (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-8 text-center">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-slate-400">No patients in hotlist with current filters</p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredHotlist.map((patient) => {
                    const priorityConfig = PRIORITY_CONFIG[patient.priority] || PRIORITY_CONFIG.medium;
                    const categoryConfig = CATEGORY_CONFIG[patient.category] || {};
                    const CategoryIcon = categoryConfig.icon || AlertCircle;
                    
                    return (
                      <Card 
                        key={`${patient.patient_id}-${patient.category}`}
                        className={`bg-slate-800/50 border-l-4 ${
                          patient.priority === 'critical' ? 'border-l-red-500' :
                          patient.priority === 'high' ? 'border-l-amber-500' :
                          'border-l-slate-600'
                        } border-slate-700 hover:bg-slate-800/70 transition-colors`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${priorityConfig.color}`}>
                                <CategoryIcon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-white">{patient.patient_name}</h3>
                                  <Badge className={`${priorityConfig.color} text-white text-xs`}>
                                    {priorityConfig.label}
                                  </Badge>
                                  <Badge variant="outline" className={`text-xs ${categoryConfig.color}`}>
                                    {categoryConfig.label}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-400">{patient.reason}</p>
                                {patient.days_overdue > 0 && (
                                  <p className="text-xs text-red-400 mt-1">
                                    {patient.days_overdue} days overdue
                                  </p>
                                )}
                                {patient.due_date && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Due: {new Date(patient.due_date).toLocaleDateString()}
                                  </p>
                                )}
                                {patient.actions_needed?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {patient.actions_needed.slice(0, 3).map((action, i) => (
                                      <Badge key={i} variant="outline" className="text-xs">
                                        {action}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openQuickAction(patient, 'schedule_visit')}
                              >
                                <CalendarPlus className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openQuickAction(patient, 'flag_for_idg')}
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400"
                                onClick={() => openQuickAction(patient, 'escalate')}
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => navigate(`/hos?patient=${patient.patient_id}`)}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Caseload Tab */}
          <TabsContent value="caseload">
            <div className="flex gap-4 mb-4">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compliance_score">Compliance Score</SelectItem>
                  <SelectItem value="overdue_tasks">Overdue Tasks</SelectItem>
                  <SelectItem value="risk_level">Risk Level</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {caseload.map((patient) => {
                  const riskConfig = RISK_CONFIG[patient.risk_level] || RISK_CONFIG.low;
                  const RiskIcon = riskConfig.icon;
                  
                  return (
                    <Card 
                      key={patient.patient_id}
                      className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${riskConfig.color}`}>
                              <RiskIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-white">{patient.patient_name}</h3>
                              <p className="text-sm text-slate-400 capitalize">
                                {patient.current_phase.replace('_', ' ')} • BP{patient.benefit_period || 1}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <p className="text-xs text-slate-500">Compliance</p>
                              <p className={`font-semibold ${
                                patient.compliance_score >= 80 ? 'text-green-400' :
                                patient.compliance_score >= 50 ? 'text-amber-400' : 'text-red-400'
                              }`}>
                                {patient.compliance_score.toFixed(0)}%
                              </p>
                            </div>
                            
                            <div className="text-center">
                              <p className="text-xs text-slate-500">Open Tasks</p>
                              <p className="font-semibold text-white">{patient.open_tasks}</p>
                            </div>
                            
                            <div className="text-center">
                              <p className="text-xs text-slate-500">Overdue</p>
                              <p className={`font-semibold ${patient.overdue_tasks > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                {patient.overdue_tasks}
                              </p>
                            </div>
                            
                            <div className="w-32">
                              <Progress 
                                value={patient.compliance_score} 
                                className="h-2"
                              />
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={() => navigate(`/hos?patient=${patient.patient_id}`)}
                            >
                              View
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    Today's Schedule
                  </CardTitle>
                  <CardDescription>
                    {visits?.today || 0} visits scheduled for today
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {visits?.scheduled_today?.length > 0 ? (
                    <div className="space-y-3">
                      {visits.scheduled_today.map((visit, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded">
                          <div>
                            <p className="font-medium text-white">{visit.patient_name}</p>
                            <p className="text-sm text-slate-400">{visit.visit_type} • {visit.scheduled_time || 'TBD'}</p>
                          </div>
                          <Badge variant={visit.status === 'completed' ? 'default' : 'outline'}>
                            {visit.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">No visits scheduled for today</p>
                  )}
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    This Week
                  </CardTitle>
                  <CardDescription>
                    {visits?.this_week || 0} total visits • {visits?.overdue || 0} overdue
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {visits?.upcoming_week?.length > 0 ? (
                    <div className="space-y-3">
                      {visits.upcoming_week.slice(0, 8).map((visit, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded">
                          <div>
                            <p className="font-medium text-white">{visit.patient_name}</p>
                            <p className="text-sm text-slate-400">
                              {visit.visit_type} • {new Date(visit.scheduled_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="outline">{visit.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-8">No upcoming visits</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Alerts Tab */}
          <TabsContent value="alerts">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-red-900/20 border-red-600/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{alertsSummary?.critical || 0}</p>
                  <p className="text-sm text-red-300">Critical Alerts</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-900/20 border-amber-600/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{alertsSummary?.high || 0}</p>
                  <p className="text-sm text-amber-300">High Priority</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-900/20 border-blue-600/30">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">{alertsSummary?.warning || 0}</p>
                  <p className="text-sm text-blue-300">Warnings</p>
                </CardContent>
              </Card>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {predictiveAlerts.length === 0 ? (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-8 text-center">
                      <Brain className="w-12 h-12 text-teal-400 mx-auto mb-4" />
                      <p className="text-slate-400">No predictive alerts at this time</p>
                    </CardContent>
                  </Card>
                ) : (
                  predictiveAlerts.map((alert) => (
                    <Card 
                      key={alert.alert_id}
                      className={`bg-slate-800/50 border-l-4 ${
                        alert.severity === 'critical' ? 'border-l-red-500' :
                        alert.severity === 'high' ? 'border-l-amber-500' :
                        'border-l-blue-500'
                      } border-slate-700`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              alert.severity === 'critical' ? 'bg-red-500' :
                              alert.severity === 'high' ? 'bg-amber-500' : 'bg-blue-500'
                            }`}>
                              <Brain className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-white">{alert.patient_name}</h3>
                                <Badge className={`text-xs ${
                                  alert.severity === 'critical' ? 'bg-red-500' :
                                  alert.severity === 'high' ? 'bg-amber-500' : 'bg-blue-500'
                                }`}>
                                  {alert.severity}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {alert.alert_type.replace('_', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-300">{alert.prediction}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                Confidence: {(alert.confidence * 100).toFixed(0)}%
                              </p>
                              {alert.recommended_actions?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Recommended Actions:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {alert.recommended_actions.map((action, i) => (
                                      <Badge key={i} variant="outline" className="text-xs">
                                        {action}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => navigate(`/hos?patient=${alert.patient_id}`)}
                          >
                            Review
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>

      {/* Quick Action Dialog */}
      <Dialog open={quickActionDialog} onOpenChange={setQuickActionDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'schedule_visit' && 'Schedule Visit'}
              {actionType === 'flag_for_idg' && 'Flag for IDG Discussion'}
              {actionType === 'escalate' && 'Escalate Patient'}
              {actionType === 'update_poc' && 'Update Plan of Care'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPatient && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-800 rounded">
                <p className="font-semibold text-white">{selectedPatient.patient_name}</p>
                <p className="text-sm text-slate-400">{selectedPatient.reason}</p>
              </div>
              
              <div>
                <label className="text-sm text-slate-400">Notes</label>
                <Textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Add notes for this action..."
                  className="mt-1 bg-slate-800 border-slate-700"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickActionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={executeQuickAction} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
